import { FC, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
} from "@mui/material";
import CloudIcon from "@mui/icons-material/Cloud";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ComputerIcon from "@mui/icons-material/Computer";
import { getCategoryDisplayLabel } from "../../utils/azureRecommendationsCategoryMapping";
import { RoadmapItemDto } from "../../../../../../customer-experience/src/lib/ShiOneClient";

interface AzureRecommendationDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: RoadmapItemDto | null;
}

const AzureRecommendationDetailsDialog: FC<
  AzureRecommendationDetailsDialogProps
> = ({ open, onClose, recommendation }) => {
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<
    Record<string, boolean>
  >({});
  const metadata = recommendation?.sourceMetadata || {};

  let parsedData: any = {};
  let affectedSubscriptions: any[] = [];

  if (metadata.AzureRecommendation) {
    try {
      parsedData = JSON.parse(metadata.AzureRecommendation);
    } catch (e) {
      console.error("Failed to parse AzureRecommendation data:", e);
    }
  }

  // Parse affectedSubscriptions if it's a string
  try {
    affectedSubscriptions = JSON.parse(metadata.affectedSubscriptions);
  } catch (e) {
    console.error("Failed to parse affectedSubscriptions:", e);
  }

  // Parse formatted description if present
  let problem = parsedData.problem || metadata.problem;
  let solution = parsedData.solution || metadata.solution;
  let details: string | undefined;
  let useFormattedDescription = false;

  // Check if description contains formatted Problem/Solution/Details
  if (recommendation?.description) {
    if (recommendation.description.includes("Problem:") && recommendation.description.includes("Solution:")) {
      const descParts = recommendation.description.split("\n\n");
      descParts.forEach((part) => {
        if (part.startsWith("Problem: ")) {
          problem = part.replace("Problem: ", "").trim();
        } else if (part.startsWith("Solution: ")) {
          solution = part.replace("Solution: ", "").trim();
        } else if (part.startsWith("Details: ")) {
          details = part.replace("Details: ", "").trim();
        }
      });
    } else {
      // Fallback: use the description as-is but preserve line breaks
      useFormattedDescription = true;
    }
  }

  const category = parsedData.category || metadata.category;
  const impact = parsedData.impact || metadata.impact;
  const totalAffectedSubscriptions =
    parsedData.totalAffectedSubscriptions ||
    metadata.totalAffectedSubscriptions ||
    affectedSubscriptions.length;
  const totalAffectedResources =
    parsedData.totalAffectedResources ||
    metadata.totalAffectedResources ||
    affectedSubscriptions.reduce(
      (acc, sub) => acc + (sub.affectedResources?.length || 0),
      0,
    );

  const getImpactChipColor = (impactLevel: string | undefined) => {
    if (impactLevel === "High") return "error";
    if (impactLevel === "Medium") return "warning";
    return "default";
  };

  const toggleSubscription = (subscriptionId: string) => {
    setExpandedSubscriptions((prev) => ({
      ...prev,
      [subscriptionId]: !prev[subscriptionId],
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h6">Azure Recommendation Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {category && (
              <Chip
                label={getCategoryDisplayLabel(category)}
                size="small"
                variant="outlined"
              />
            )}
            {impact && (
              <Chip
                label={`Impact: ${impact}`}
                sx={{ borderColor: getImpactChipColor(impact) }}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {useFormattedDescription && recommendation?.description ? (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Description
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line' }}
              >
                {recommendation.description}
              </Typography>
            </Box>
          ) : (
            <>
              {problem && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Problem
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {problem}
                  </Typography>
                </Box>
              )}

              {solution && solution !== problem && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Solution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {solution}
                  </Typography>
                </Box>
              )}

              {details && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {details}
                  </Typography>
                </Box>
              )}
            </>
          )}

          <Divider />

          <Box sx={{ display: "flex", gap: 4 }}>
            {totalAffectedSubscriptions !== undefined &&
              totalAffectedSubscriptions !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Affected Subscriptions
                  </Typography>
                  <Typography variant="h6">
                    {totalAffectedSubscriptions}
                  </Typography>
                </Box>
              )}
            {totalAffectedResources !== undefined &&
              totalAffectedResources !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Affected Resources
                  </Typography>
                  <Typography variant="h6">{totalAffectedResources}</Typography>
                </Box>
              )}
          </Box>

          {/* Show affected resources list if this is an aggregated recommendation */}
          {affectedSubscriptions.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Affected Resources by Subscription
                </Typography>
                <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
                  {affectedSubscriptions.map((subscription) => (
                    <Box key={subscription.subscriptionId}>
                      <ListItem
                        onClick={() =>
                          toggleSubscription(subscription.subscriptionId)
                        }
                        sx={{ pl: 0 }}
                      >
                        <IconButton size="small">
                          {expandedSubscriptions[
                            subscription.subscriptionId
                          ] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                              }}
                            >
                              {subscription.subscriptionId}
                            </Typography>
                          }
                          secondary={`${subscription.affectedResources?.length || 0} resources`}
                        />
                      </ListItem>
                      <Collapse
                        in={expandedSubscriptions[subscription.subscriptionId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {subscription.affectedResources?.map(
                            (resource: any) => (
                              <ListItem
                                key={resource.resourceId}
                                sx={{ pl: 6 }}
                              >
                                <ComputerIcon
                                  sx={{
                                    mr: 1,
                                    fontSize: 18,
                                    color: "text.secondary",
                                  }}
                                />
                                <ListItemText
                                  primary={resource.resourceName}
                                  secondary={
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {resource.resourceType
                                        ?.toLowerCase()
                                        .replace("microsoft.", "")}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ),
                          )}
                        </List>
                      </Collapse>
                    </Box>
                  ))}
                </List>
              </Box>
            </>
          )}

          <Box
            sx={{
              backgroundColor: "action.hover",
              borderRadius: 1,
              p: 2,
              mt: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              For more details, view your{" "}
              <Link
                href="/cloud/azure/recommendations"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Azure Recommendations
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AzureRecommendationDetailsDialog;
